import React, { useEffect, useRef, useState } from 'react';
import type { Node } from 'reactflow';
import { Box, Typography, Button, Paper, Alert } from '@mui/material';
import type { PrimitiveNodeData } from './PrimitiveNode';
import {
  getRunTestCurrentNode,
  getRunTestStatus,
  sendRunTestSignal,
} from '../../../services/workflowConfigService';

const POLL_INTERVAL_MS = 1500;
const TERMINAL_FAILURE_STATUSES = new Set(['FAILED', 'TERMINATED', 'CANCELED', 'TIMED_OUT', 'NOT_FOUND']);

interface WorkflowRunTestPanelProps {
  nodes: Node<PrimitiveNodeData>[];
  onCurrentNodeChange: (nodeId: string | null) => void;
  /** Run identifier from a run-test already started elsewhere (the canvas's Dry run/Execute
   * buttons calling startRunTest directly) — this panel is purely a status/signal viewer for
   * that run; it never starts one itself. */
  initialRun?: { workflowId: string; refId: string } | null;
}

type TrafficLight = { color: 'grey' | 'green' | 'red' | 'yellow'; label: string };

function computeTrafficLight(status: string | null, waitingOnSignal: boolean): TrafficLight {
  if (!status) return { color: 'grey', label: 'Not started' };
  if (status === 'COMPLETED') return { color: 'green', label: 'Done' };
  if (TERMINAL_FAILURE_STATUSES.has(status)) return { color: 'red', label: `Blocked (${status})` };
  if (waitingOnSignal) return { color: 'yellow', label: 'Waiting for approval/decision' };
  return { color: 'yellow', label: 'In progress' };
}

const DOT_COLORS: Record<TrafficLight['color'], string> = {
  grey: '#9e9e9e',
  green: '#2e7d32',
  red: '#c62828',
  yellow: '#f9a825',
};

/** Run-test status/signal viewer: poll current-node + status → signal a paused WAIT_SIGNAL.
 * Starting a run is the canvas's own Dry run/Execute buttons' job (WorkflowCanvasTab.tsx); this
 * component only ever watches a run that's already in progress, via `initialRun`. */
const WorkflowRunTestPanel: React.FC<WorkflowRunTestPanelProps> = ({
  nodes,
  onCurrentNodeChange,
  initialRun,
}) => {
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [refId, setRefId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const currentNode = nodes.find((n) => n.id === currentNodeId) ?? null;
  const waitingOnSignal = currentNode?.data.primType === 'WAIT_SIGNAL';
  const light = computeTrafficLight(status, waitingOnSignal);

  const poll = async (id: string) => {
    try {
      const [cn, st] = await Promise.all([getRunTestCurrentNode(id), getRunTestStatus(id)]);
      setCurrentNodeId(cn.current_node_id);
      onCurrentNodeChange(cn.current_node_id);
      setStatus(st.status);
      if (st.status === 'COMPLETED' || TERMINAL_FAILURE_STATUSES.has(st.status)) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch {
      // Transient failure: keep last known state, next tick corrects it (ref: spec §6 known gap).
    }
  };

  // Seeds workflowId/refId from the run-test the canvas's Dry run/Execute buttons already
  // started, so the polling effect below picks it up immediately.
  useEffect(() => {
    if (!initialRun) return;
    setWorkflowId(initialRun.workflowId);
    setRefId(initialRun.refId);
    setStatus(null);
    setCurrentNodeId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRun?.workflowId, initialRun?.refId]);

  useEffect(() => {
    if (!workflowId) return;
    poll(workflowId);
    intervalRef.current = setInterval(() => poll(workflowId), POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId]);

  const handleSignal = async (action: 'approved' | 'rejected') => {
    if (!workflowId || !currentNode) return;
    const signalName = currentNode.data.config.signal_name as string | undefined;
    if (!signalName) {
      setError('Current WAIT_SIGNAL node has no signal_name configured');
      return;
    }
    setError(null);
    try {
      await sendRunTestSignal(workflowId, { signal_name: signalName, payload: { action } });
      poll(workflowId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send signal');
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Run test
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {workflowId ? (
        <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, boxShadow: 1, backgroundColor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: DOT_COLORS[light.color] }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {light.label}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            workflow_id: {workflowId}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: currentNodeId ? 0 : 1 }}>
            ref_id: {refId}
          </Typography>
          {currentNodeId && (
            // NEW — current_node_id from GET /run-test/{id}/current-node, the actual node the
            // run is at (or stopped at). That endpoint has no status field of its own — status
            // above still comes from the separate /status endpoint — this just says where.
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Current node: {currentNode?.data.name ?? currentNodeId}
              {currentNode?.data.name ? ` (${currentNodeId})` : ''}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={() => poll(workflowId)} sx={{ textTransform: 'none' }}>
              Check status
            </Button>
            {waitingOnSignal && (
              <>
                <Button size="small" color="success" variant="outlined" onClick={() => handleSignal('approved')} sx={{ textTransform: 'none' }}>
                  Approve
                </Button>
                <Button size="small" color="error" variant="outlined" onClick={() => handleSignal('rejected')} sx={{ textTransform: 'none' }}>
                  Reject
                </Button>
              </>
            )}
          </Box>
        </Paper>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No run in progress. Click "Dry run" or "Execute" on the canvas to start one.
        </Typography>
      )}
    </Box>
  );
};

export default WorkflowRunTestPanel;
